/**
 * NGTDialogPlayer.js
 *
 * Created on Oct 2, 2013, 12:30:31 PM
 *
 * @author Javier Quevedo-Fernandez for Ranj
 **/

import AudioContent from "../../../../lib/html5-software-library/src/ranj/eng/content/AudioContent";
import ChatContent from "../../../../lib/html5-software-library/src/ranj/eng/content/ChatContent";
import ContentTypes from "../../../../lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import ConversationTopicContent from "../../../../lib/html5-software-library/src/ranj/eng/content/dialog/ConversationTopicContent";
import TextContent from "../../../../lib/html5-software-library/src/ranj/eng/content/TextContent";
import DialogContentPackage from "../../../../lib/html5-software-library/src/ranj/eng/dialog/framework/content/DialogContentPackage";
import DefaultDialogPlayerEvent from "../../../../lib/html5-software-library/src/ranj/eng/dialog/framework/player/DefaultDialogPlayerEvent";
import HierarchicalDialogPlayer from "../../../../lib/html5-software-library/src/ranj/eng/dialog/framework/player/HierarchicalDialogPlayer";
import PlayMode from "../../../../lib/html5-software-library/src/ranj/eng/dialog/framework/player/PlayMode";
import { isNull } from "../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import GoogleAnalyticsEvent from "../../analytics/GoogleAnalyticsEvent";
import TrackString from "../../analytics/TrackString";
import ChapterLibrary from "../../app/chapters/model/ChapterLibrary";
import CharacterLibrary from "../../app/characters/model/CharacterLibrary";
import ConversationFeedbackEvent from "../../app/conversation-feedback/event/ConversationFeedbackEvent";
import ConversationPlayer from "../../app/conversation/controller/ConversationPlayer";
import ConversationPlayerFactory from "../../app/conversation/controller/ConversationPlayerFactory";
import ConversationFinishedEvent from "../../app/conversation/event/ConversationFinishedEvent";
import LlmConversationMessage, { LlmMessageSender } from "../../app/conversation/model/LlmConversationMessage";
import ProgressModel from "../../app/progress/model/ProgressModel";
import { StoryVariables } from "../../app/variables/StoryVariables";
import GameEvent from "../../core/application/GameEvent";
import NGTConfigKey from "../base/NGTConfigKey";
import NGTVariableManager from "../base/NGTVariableManager";
import NPCModel from "../scenario/NPCModel";
import NGTDialogPlayerEvent from "./NGTDialogPlayerEvent";

export default class NGTDialogPlayer {
    constructor(variableManager, playSessionModel, languagePack, config, dispatcher, dialogFactory, conversationPlayerFactory, progressModel, chapterLibrary, npcModel, characterLibrary) {
        /** @type {soma.EventDispatcher} */
        this._dispatcher = dispatcher;
        this.eventDispatcher = this._dispatcher; // For backwards compatibility.
        this._dialogId = undefined;
        //this._dialogPlayer = new DefaultDialogPlayer(100);
        this._dialogPlayer = new HierarchicalDialogPlayer(dialogFactory, 100);
        /** @type {ConversationPlayerFactory} */
        this._conversationPlayerFactory = conversationPlayerFactory;
        /** @type {ProgressModel} */
        this._progressModel = progressModel;
        /** @type {ChapterLibrary} */
        this._chapterLibrary = chapterLibrary;
        /** @type {NPCModel} */
        this._npcModel = npcModel;
        /** @type {CharacterLibrary} */
        this._characterLibrary = characterLibrary;

        this._currentOptions = [];
        this._choicesMade = [];
        this._openContent = [];
        this._chosenTexts = [];
        this._contentDelayedOptionMap = {};

        this._handlePauseGameBound = this._handlePauseGame.bind(this);
        this._handleResumeGameBound = this._handleResumeGame.bind(this);

        // Keep a reference to the injected objects
        /** @type {NGTVariableManager} */
        this._variableManager = variableManager;
        this._playSessionModel = playSessionModel;
        this._languagePack = languagePack;
        this._config = config;

        /** @type {ConversationPlayer} */
        this._llmConversationPlayer = null;
        /** @type {ChatContent} */
        this._currentChatContent = null;

        /** @type {LlmConversationMessage[]} */
        this._llmContextMessages = [];
        /** @type {LlmConversationMessage[]} */
        this._scriptedConversationHistory = [];

        this._handleChatContentFinishedBound = this._handleChatContentFinished.bind(this);
    }

    destroy() {
        this.removeEventListeners();
        this._dialogPlayer.destroy();
        this._dialogPlayer = null;
    }

    getContent() {
        return this._content;
    }

    setContent(content) {
        this._content = content;
    }

    getContentPlayer() {
        return this._contentPlayerDelegate;
    }

    setContentPlayer(contentPlayer) {
        this._contentPlayerDelegate = contentPlayer;
    }

    /**
     * @returns {HierarchicalDialogPlayer}
     */
    getDialogPlayer() {
        return this._dialogPlayer;
    }

    setDialog(dialogId, dialog) {
        this._dialogId = dialogId;
        this._dialogPlayer.setDialog(dialog, this._variableManager.solve.bind(this._variableManager));
        this._dialogPlayer.setPlayMode(PlayMode.COMMANDS);
    }

    viewContent() {
        this._autoVoiceOverEnabled = this._config.getValue(NGTConfigKey.USE_AUTO_VOICEOVER);
        this.addEventListeners();
        this._dialogPlayer.next();
    }

    hasPreviousCommandSequence() {
        return this._dialogPlayer.hasPreviousCommandSequence();
    }

    gotoPreviousCommandSequence() {
        this._dialogPlayer.gotoPreviousCommandSequence();
    }

    stop() {
        this.removeEventListeners();
    }

    contentCompleted(content) {
        // Check whether the given content was indeed open in the dialog player
        // and advance if all open content has been completed.
        // Only case where more than one content may be open is if the CTD dialog player
        // adds content to the content coming from the underlying Ranj dialog player.

        const foundContent = this._openContent.contains(content);
        if (foundContent) {
            this._openContent.remove(content);
        }

        // Timeout: check if we need to take a timeout option now.
        const timeoutTaken = this.checkTakeTimeoutOption();
        if (timeoutTaken) {
            return;
        }

        // Handling:
        // - If this content delayed the picking of an option, pick it now
        // - If this content was "just" open, do a next() on the dialog player
        if (this._contentDelayedOptionMap[content]) {
            const option = this._contentDelayedOptionMap[content];
            delete this._contentDelayedOptionMap[content];
            this._dialogPlayer.takeOption(option);
        } else {
            if (this._openContent.length === 0 && foundContent) {
                // All open content is completed: next!
                this._dialogPlayer.next();
            }
        }
    }

    submitPlayerInput(value) {
        this._llmConversationPlayer.submitPlayerInput(value);
    }

    getCurrentOptions() {
        return this._currentOptions;
    }

    optionChosen(option) {
        this.processOptionConsequences(option);
        // Auto-audio
        const content = option.getData().getData().content;
        const contentType = content.getType();
        if (contentType === ContentTypes.TYPE_TEXT) {
            this._chosenTexts.push(content);
        }

        // Auto-audio 
        //RF201808: why this convention of auto-audio when a contentId is present??? This claims the contentID which can no longer be used for other purposes:
        //  When we want to use the contentID for e.g. analytics, we get errors about missing audio
        const contentId = content.getContentID();
        if (this._autoVoiceOverEnabled && contentType === ContentTypes.TYPE_TEXT && contentId != null && contentId.length > 0) {
            // Hold this option to be picked when the auto-audio content has completed.
            const audioContent = this.addAutoVoiceOver(contentId);
            this._contentDelayedOptionMap[audioContent] = option;
        } else {
            // Pick the option immediately.
            this._dialogPlayer.takeOption(option);
        }
        // Log the option
        this._trackOptionChosen(content);

        // For the outside world, the option is just chosen (i.e. option buttons can be removed, option panels can be made invisible)
        // regardless of whether audio is being played before the dialog advances.
        const dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_OPTION_CHOSEN, null, option);
        this._dispatchDialogPlayerEvent(dialogPlayerEvent.type, { data: dialogPlayerEvent });

        this.clearOptions();
    }

    addEventListeners() {

        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.START_DIALOG, this.handleStartDialog, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.CONTENT, this.handleDialogContent, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.SCORE, this.handleDialogScore, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_COMMAND, this.handleDialogCommand, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_OPTION, this.handleDialogOption, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.EXIT_DIALOG_STATE, this.handleExitDialogState, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_OPTION_TAKEN, this.handleAutoTakeOptionTaken, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_TIME_TICK, this.handleAutoTakeTimeTick, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_TICK, this.handleDialogTimeTick, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_OUT, this.handleDialogTimeOut, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.END_DIALOG, this.handleEndDialog, this);
        this._dialogPlayer.addEventListener(DefaultDialogPlayerEvent.COMMAND_SEQUENCE_END, this.handleCommandSequenceEnd, this);

        // Listen to game pause/resume
        this._dispatcher.addEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this._dispatcher.addEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);

    }

    removeEventListeners() {

        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.START_DIALOG, this.handleStartDialog, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.CONTENT, this.handleDialogContent, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.SCORE, this.handleDialogScore, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_COMMAND, this.handleDialogCommand, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_OPTION, this.handleDialogOption, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.EXIT_DIALOG_STATE, this.handleExitDialogState, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_OPTION_TAKEN, this.handleAutoTakeOptionTaken, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.AUTO_TAKE_TIME_TICK, this.handleAutoTakeTimeTick, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_TICK, this.handleDialogTimeTick, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_OUT, this.handleDialogTimeOut, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.END_DIALOG, this.handleEndDialog, this);
        this._dialogPlayer.removeEventListener(DefaultDialogPlayerEvent.COMMAND_SEQUENCE_END, this.handleCommandSequenceEnd, this);

        this._dispatcher.removeEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this._dispatcher.removeEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);

    }

    handleStartDialog(event) {
        console.info("Dialog started.", this._dialogId);
    }

    _handlePauseGame(event) {
        this.setPaused(true);
    }

    _handleResumeGame(event) {
        this.setPaused(false);
    }

    setPaused(value) {
        if (value) {
            this._dialogPlayer.pauseTimers();
        } else {
            this._dialogPlayer.resumeTimers();
        }
    }

    handleDialogContent({ data, option }) {
        const content = data.content;

        // Remember this content as open (so that completion can lead to next())
        this._openContent.push(content);

        switch (content.getType()) {
            case ContentTypes.TYPE_SET_VARIABLE:
                this.handleSetVariableContent(content);
                break;
            case ContentTypes.TYPE_SET_NUMBER:
                this.handleSetNumberContent(content);
                break;
            case ContentTypes.TYPE_TEXT:
                /** @type {TextContent} */
                const textContent = content;
                if (this._dispatcher.hasEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT)) {
                    //Auto audio
                    var dialogContentPackage = data;
                    var contentID = textContent.getContentID();
                    if (this._autoVoiceOverEnabled && !isNull(contentID) && contentID.length > 0) {
                        this.addAutoVoiceOver(contentID);
                    }

                    // Dispatch text content AFTER auto-audio, otherwise the underlying dialog will advance even before the audio is finished.
                    var dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_CONTENT, data, option);
                    this._dispatchDialogPlayerEvent(dialogPlayerEvent.type, { data: dialogPlayerEvent });

                    // Remember this text as the "current situation"
                    this._currentSituation = (contentID && contentID.length > 0) ? contentID : textContent.getText();
                } else {
                    this.ignoreContent(content);
                }

                // Handle chat history that is seen by the LLM, but not by the player.
                // Useful for providing context to the LLM.
                if (textContent.getBalloonType() === `llm`) {
                    const kickoffAssistantMessage = textContent.getText();
                    this._llmContextMessages.push(new LlmConversationMessage(LlmMessageSender.ASSISTANT, kickoffAssistantMessage));
                    this.contentCompleted(textContent);
                }
                break;
            case ContentTypes.TYPE_CONVERSATION_TOPIC:
                /** @type {ConversationTopicContent} */
                const conversationTopicContent = content;
                // Remember this text as the "current situation"
                const topicDescription = conversationTopicContent.getTopicDescription();
                var contentID = conversationTopicContent.getContentID();
                if (!isNull(contentID) && contentID.length > 0) {
                    this._currentSituation = contentID;
                }
                else if (!isNull(topicDescription) && topicDescription.length > 0) {
                    this._currentSituation = topicDescription;
                }

                // Keep track of the scripted conversation history.
                const characterName = conversationTopicContent.getTopicNewCondition();
                const characterMessage = conversationTopicContent.getTopicDescription();
                this._scriptedConversationHistory.push(new LlmConversationMessage(LlmMessageSender.ASSISTANT, `${characterName}: ${characterMessage}`));

                if (this._dispatcher.hasEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT)) {
                    var dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_CONTENT, data, option);
                    this._dispatchDialogPlayerEvent(dialogPlayerEvent.type, { data: dialogPlayerEvent });
                } else {
                    this.ignoreContent(content);
                }

                break;
            case ContentTypes.TYPE_AUDIO:
                if (this._dispatcher.hasEventListener(NGTDialogPlayerEvent.DIALOG_AUDIO)) {
                    var dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_AUDIO, data, option);
                    this._dispatchDialogPlayerEvent(dialogPlayerEvent.type, { data: dialogPlayerEvent });
                } else {
                    this.ignoreContent(content);
                }
                break;
            case ContentTypes.TYPE_IMAGE:
            case ContentTypes.TYPE_OVERLAY_IMAGE:
            case ContentTypes.TYPE_MOVIE:
                if (this._dispatcher.hasEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT)) {
                    var dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_CONTENT, data, option);
                    this._dispatchDialogPlayerEvent(dialogPlayerEvent.type, { data: dialogPlayerEvent });
                } else {
                    this.ignoreContent(content);
                }
                break;
            case ContentTypes.TYPE_CHAT:
                this._handleChatContent(content);
                break;
            default:
                this.contentCompleted(content);
                break;
        }
    }

    handleSetVariableContent(content) {
        const variableMutations = content.getVariableMutations();
        for (let i = 0; i < variableMutations.length; i++) {
            const mut = variableMutations[i];
            this._variableManager.setVariable(mut.variableName, mut.value, mut.mutationType);
        }

        this.contentCompleted(content);
    }

    handleSetNumberContent(content) {
        this._variableManager.setNumber(content.name, content.value, content.operator);
        this.contentCompleted(content);
    }

    /**
     * 
     * @param {ChatContent} content 
     */
    async _handleChatContent(content) {
        const chapterId = this._chapterLibrary.getCurrentChapter().id;
        const conversationId = content.getChatDialogId();

        this._currentChatContent = content;

        this._llmConversationPlayer = this._conversationPlayerFactory.create();

        // Build the starting conversation history.
        // Squash scripted conversation history into a single message to keep it as consistent with the original approach.
        const startingConversationHistory = [];
        if (this._llmContextMessages.length > 0) {
            const squashedLlmContextMessages = this._llmContextMessages.map((message) => message.message).join("\n");
            startingConversationHistory.push(new LlmConversationMessage(LlmMessageSender.ASSISTANT, squashedLlmContextMessages));
        }
        if (this._scriptedConversationHistory.length > 0) {
            const squashedScriptedConversationHistory = this._scriptedConversationHistory.map((message) => message.message).join("\n");
            startingConversationHistory.push(new LlmConversationMessage(LlmMessageSender.ASSISTANT, squashedScriptedConversationHistory));
        }

        // Get the characters for the conversation.
        const definedNpcs = this._npcModel.getNPCs();
        const characters = definedNpcs.map(npc => {
            const characterId = npc.getNpcID()?.trim();
            return this._characterLibrary.getCharacterById(characterId);
        });

        // Start the conversation.
        await this._llmConversationPlayer.start(chapterId, conversationId, characters, startingConversationHistory);
        await this._llmConversationPlayer.next();

        this._dispatcher.addEventListener(ConversationFinishedEvent.PERFORM, this._handleChatContentFinishedBound);
    }

    _handleChatContentFinished(evt) {
        this._dispatcher.removeEventListener(ConversationFinishedEvent.PERFORM, this._handleChatContentFinishedBound);

        /** @type {ConversationFeedbackEvent} */
        const data = evt.params.data;
        const { conversation } = data;
        this._progressModel.saveConversationProgress(conversation.id, conversation);

        console.log(`Setting outcome variable: ${conversation.outcome}`);
        this._variableManager.setVariable(`${StoryVariables.OUTCOME_PREFIX}${conversation.outcome}`, true);

        // Track outcome in analytics.
        const eventName = TrackString.EVENT_CONVERSATION_OUTCOME;
        const eventParameters = TrackString.createConversationOutcomeParams({
            outcomeId: conversation.outcome,
        });
        this._dispatcher.dispatch(GoogleAnalyticsEvent.EVENT, { name: eventName, params: eventParameters });

        // Clean up.
        this._llmConversationPlayer.destroy();
        this._llmConversationPlayer = null;

        // Complete the chat content, so that the dialog player can continue.
        this.contentCompleted(this._currentChatContent);
        this._currentChatContent = null;
    }

    handleDialogScore({ data }) {
        this.contentCompleted(data);
    }

    handleDialogCommand({ data }) {
        this.contentCompleted(data);
    }

    handleDialogOption({ data }) {
        this._dialogPlayer.next();

        // Add if not an autotake option
        if (data.option.getData() !== null && data.option.getData().content !== null) {
            this.addOption(data.option);
            const dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_OPTION, data.data, data.option);
            this._dispatchDialogPlayerEvent(dialogPlayerEvent.type, { data: dialogPlayerEvent });
        }
    }

    processOptionConsequences(option) {
        // Do whatever is necessary because this option was chosen.
        if (!option)
            return;
        const data = option.getData();
        if (!data)
            return;
        const dataData = data.getData();
        if (!dataData)
            return;
        const content = dataData.content;
        if (!content)
            return;

        if (content.getType() === ContentTypes.TYPE_KEY_MOMENT_CHOICE) {
            // Process variables
            const varMutations = content.getScoreVariables();
            for (let i = 0; i < varMutations.length; i++) {
                const mutation = varMutations[i]; // mutation is a VariableMutationNumberContent
                this._variableManager.setVariable(mutation.getVariableName(), mutation.getValue(), mutation.getMutationType());
            }

            // Remember key moment choices made in this dialog
            this._choicesMade.push(content);
        }
    }

    _trackOptionChosen(optionContent) {
        const situation = this._currentSituation;
        const content = this.getContent();
        const contentType = content.getType();
        const dialogId = this._dialogId;

        if (optionContent.getType() === ContentTypes.TYPE_TEXT) {
            /** @type {TextContent} */
            const textContent = optionContent;
            const contentId = optionContent.getContentID();

            // Log contentId if it exists, otherwise log the text of TextContent
            const currentChoice = (contentId && contentId.length > 0) ? contentId : textContent.getText();

            // Log the option
            switch (contentType) {
                case ContentTypes.TYPE_DIALOG:
                    this._dispatchDialogPlayerEvent(GoogleAnalyticsEvent.EVENT, {
                        name: TrackString.EVENT_DIALOG_CHOICE,
                        params: TrackString.createDialogChoiceParams(dialogId, situation, currentChoice, contentId)
                    });
                    break;
                case ContentTypes.TYPE_DECISION:
                    this._dispatchDialogPlayerEvent(GoogleAnalyticsEvent.EVENT, {
                        name: TrackString.EVENT_DECISION_CHOICE,
                        params: TrackString.createDialogChoiceParams(dialogId, situation, currentChoice, contentId)
                    });
                    break;
            }
        }
        else if (optionContent.getType() === ContentTypes.TYPE_KEY_MOMENT_CHOICE) {
            //optionContent is KeyMomentChoiceContent;
            let contentId = optionContent.getContentID();
            let currentChoice = (contentId && contentId.length > 0) ? contentId : optionContent.getChoice();

            this._dispatchDialogPlayerEvent(GoogleAnalyticsEvent.EVENT, {
                name: TrackString.EVENT_KEY_MOMENT_CHOICE,
                params: TrackString.createDialogChoiceParams(dialogId, situation, currentChoice, contentId)
            });
        }
    }

    addAutoVoiceOver(audioID) {
        const audioContent = new AudioContent(this._languagePack, null, audioID);
        const audioDialogContentPackage = new DialogContentPackage(this._languagePack, audioContent, null);

        // Audio content is now also "open"
        this._openContent.push(audioContent);

        let dialogPlayerEvent = new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_CONTENT, audioDialogContentPackage, null);
        this._dispatchDialogPlayerEvent(dialogPlayerEvent.type, { data: dialogPlayerEvent });
        return audioContent;
    }

    handleExitDialogState(event) {
        this.clearOptions();
    }

    handleAutoTakeTimeTick({ data }) {
        this._dispatchDialogPlayerEvent(NGTDialogPlayerEvent.ANSWER_TIMER, new NGTDialogPlayerEvent(NGTDialogPlayerEvent.ANSWER_TIMER, data));
    }

    handleDialogTimeTick({ data }) {
        this._dispatchDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_TIMER, new NGTDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_TIMER, this._dialogPlayer.getMaxTime() - data));
    }

    handleDialogTimeOut({ data }) {
        this._timeoutOption = data;
        this.checkTakeTimeoutOption();
    }

    handleEndDialog(event) {

        // Process choices made (if any)
        for (let i = 0; i < this._choicesMade.length; i++) {
            const kmChoiceContent = this._choicesMade[i];
            this._playSessionModel.getCurrentScenarioSession().addKeyMomentEntry(kmChoiceContent);
        }
        this.removeEventListeners();
        this._dispatchDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_END);

        // Dialog is now completed
        this.getContentPlayer().contentCompleted(this.getContent());
    }

    handleCommandSequenceEnd(evt) {
        this._dispatchDialogPlayerEvent(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END);
    }

    handleAutoTakeOptionTaken({ data }) {
        this.processOptionConsequences(data.option);
        console.warn("WARNING: auto take option is not tracked in the OZ metrics yet.");
    }

    addOption(option) {
        this.getCurrentOptions().push(option);
        this._dispatchDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED);
    }

    clearOptions() {
        if (this._currentOptions.length === 0)
            return;

        this.getCurrentOptions().splice(0, this._currentOptions.length);
        this._dispatchDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_OPTIONS_CHANGED);
    }

    checkTakeTimeoutOption() {
        if (!isNull(this._timeoutOption) && this._openContent.length === 0) {
            this.takeTimeoutOption();
            return true;
        }

        return false;
    }

    takeTimeoutOption() {
        this._dispatchDialogPlayerEvent(NGTDialogPlayerEvent.DIALOG_TIME_OUT);
        this._dialogPlayer.takeOption(this._timeoutOption);
        this._timeoutOption = null;
    }

    ignoreContent(content) {
        console.warn(`WARNING: content type ${content.type} not handled by game!`);
        this.contentCompleted(content);
    }

    hasChosenText(content) {
        for (let i = 0; i < this._chosenTexts.length; i++) {
            if (this._chosenTexts[i] === content) //maybe use other compare on string or ID
            {
                return true;
            }
        }
        return false;
    }

    _dispatchDialogPlayerEvent(type, dataObj = {}) {
        dataObj.contentPlayer = this;

        this._dispatcher.dispatch(type, dataObj);
    }

    get llmConversationPlayer() {
        return this._llmConversationPlayer;
    }
}